import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    question: "How long does it take to see results?",
    answer:
      "Most users report noticeable improvements in energy and performance within 2-4 weeks of consistent use.",
  },
  {
    question: "Is this product safe to use?",
    answer:
      "Yes, our formula is made with natural ingredients in a GMP-certified facility. Always consult your healthcare provider before starting any supplement regimen.",
  },
  {
    question: "What's the recommended dosage?",
    answer:
      "Take 2 capsules daily with water. For enhanced results, take an additional capsule 30 minutes before activity.",
  },
  {
    question: "Are there any side effects?",
    answer:
      "Our natural formula is generally well-tolerated. As with any supplement, some individuals may experience mild digestive changes initially.",
  },
  {
    question: "Can I take this with other supplements?",
    answer:
      "While our product is safe to use with most supplements, we recommend consulting with your healthcare provider about your specific supplement regimen.",
  },
  {
    question: "What if I'm not satisfied with the results?",
    answer:
      "We offer a 100% satisfaction guarantee. If you're not completely satisfied, contact us for a full refund within 60 days of purchase.",
  },
];

const FAQItem = ({ faq, index, isVisible, isOpen, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      className="mb-4"
    >
      <div
        onClick={onToggle}
        onKeyUp={(e) => {
          if (e.key === "Enter") onToggle();
        }}
        className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 cursor-pointer"
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800 pr-4">
            {faq.question}
          </h3>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0"
          >
            <ChevronDown className="w-6 h-6 text-red-500" />
          </motion.div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <p className="mt-4 text-gray-600">{faq.answer}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const AnimatedFAQSection = () => {
  const [visibleCount, setVisibleCount] = useState(1);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("faq-section");
      if (section) {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

        if (isVisible && !hasScrolled) {
          setHasScrolled(true);
          let count = 1;

          const interval = setInterval(() => {
            if (count < faqs.length) {
              setVisibleCount((prevCount) => prevCount + 1);
              count += 1;
            } else {
              clearInterval(interval);
            }
          }, 200);

          return () => clearInterval(interval);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial scroll position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasScrolled]);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Create pairs of FAQs for the grid
  const faqPairs = [];
  for (let i = 0; i < faqs.length; i += 2) {
    faqPairs.push(faqs.slice(i, i + 2));
  }

  return (
    <section id="faq-section" className="py-20 bg-yellow-50">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="[text-shadow:1px_0_0_white,2px_0_0_white,1px_1px_0_white,2px_2px_0_white] text-6xl md:text-8xl  text-center mb-16 text-[#a41e24]"
          style={{
            fontFamily: "Headlined, sans-serif",
          }}
        >
          FAQ
          <span
            className="text-4xl md:text-6xl"
          >
            s
          </span>
        </motion.h2>

        <div className="max-w-6xl mx-auto">
          {faqPairs.map((pair, pairIndex) => (
            <div
              key={pair.map((faq) => faq.question).join("-")}
              className="flex flex-col md:flex-row md:gap-4 mb-0"
            >
              {pair.map((faq, index) => {
                const actualIndex = pairIndex * 2 + index;
                return (
                  <div key={actualIndex} className="flex-1">
                    <FAQItem
                      faq={faq}
                      index={actualIndex}
                      isVisible={actualIndex < visibleCount}
                      isOpen={openIndex === actualIndex}
                      onToggle={() => handleToggle(actualIndex)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AnimatedFAQSection;
